import React from 'react'
import { addCommasToNumber, Vertical, Horizontal, NothingMessage, Texto } from '@gravitate-js/excalibrr'
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  ComposedChart,
  ReferenceLine,
} from 'recharts'
import { LoadingOutlined } from '@ant-design/icons'
import { formatters } from '@utils/intl/dateTimeFormatters'
import moment from 'moment'
export const chartColors = ['#102643', '   #76abf3', '  #cce5ff', '#8dabc4']

export const MonthlyVolumeChart = ({ metricsLoading, chartData, isPrice }) => {
  return (
    <Vertical flex={2} className='mx-3 mb-3' background='bg-1' borderRadius={3} border='all'>
      <Horizontal className='p-3' verticalCenter flex={0}>
        <Texto weight='bold' className='flex-1' appearance='medium'>
          DIRECTIVE PERFORMANCE
        </Texto>
      </Horizontal>
      <Vertical className='p-3' flex={1}>
        {metricsLoading ? (
          <Vertical verticalCenter horizontalCenter>
            <Texto align='center' appearance='secondary' className='mb-3' category='h3'>
              <LoadingOutlined />
            </Texto>
          </Vertical>
        ) : (
          <MonthlyBarChart isPrice={isPrice} chartData={chartData} />
        )}
      </Vertical>
    </Vertical>
  )
}
const MonthlyBarChart = ({ chartData, isPrice }) => {
  if (!chartData) {
    return <NothingMessage message='No Monthly Volume. Orders will need to be scheduled to view this metric.' />
  }

  const monthlyBarConfig = {
    keys: ['loaded', 'scheduled', 'directive_loaded', 'directive_min'],
    colors: [chartColors[0], chartColors[1], chartColors[2], chartColors[2]],
  }

  return (
    <ResponsiveContainer width='100%' height='100%'>
      <ComposedChart
        width={500}
        height={400}
        data={chartData.volumes}
        margin={{
          top: 20,
        }}
      >
        <defs>
          <linearGradient id='areaColor' x1='0' y1='0' x2='0' y2='1'>
            <stop offset='5%' stopColor='#D8E5EE' stopOpacity={0.8} />
            <stop offset='95%' stopColor='#D8E5EE' stopOpacity={0.1} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke='#f5f5f5' />
        <XAxis dataKey='date' scale='band' tickFormatter={(value) => formatters.MONTH_DAY(moment(value))} />
        <YAxis tickFormatter={(value) => addCommasToNumber(value, 0)} yAxisId='left' />
        <YAxis tickFormatter={(value) => `${addCommasToNumber(value * 100, 0)}%`} yAxisId='right' orientation='right' />
        <Tooltip content={isPrice ? <PriceLoadedTooltip /> : <DirectiveLoadedTooltip />} />

        <Area type='monotone' yAxisId='right' dataKey='pace' fillOpacity={1} fill='url(#areaColor)' stroke='#D9DEE2' />
        {monthlyBarConfig.keys.map((key, i) => (
          <Bar
            key={i}
            radius={[3, 3, 0, 0]}
            dataKey={key}
            yAxisId='left'
            stackId='a'
            barSize={6}
            fill={monthlyBarConfig.colors[i]}
          />
        ))}
        <ReferenceLine
          yAxisId='left'
          y={chartData.rateableVolume || 0}
          label={<ReferenceLabel value='Rateable' fill='#c79c02' />}
          stroke='#c79c02'
          strokeDasharray='3 3'
          strokeWidth={2}
          isFront
        />
      </ComposedChart>
    </ResponsiveContainer>
  )
}
export function ReferenceLabel({ fill, value, textAnchor, fontSize, viewBox, dy, dx }) {
  const x = 0
  const y = viewBox.y + 3
  return (
    <text x={x} y={y} dy={dy} dx={dx} fill={fill} fontSize={fontSize || 10} fontWeight='bold' textAnchor={textAnchor}>
      {value}
    </text>
  )
}
const DirectiveLoadedTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className=' bg-1 p-4'>
        <Texto category='h6' appearance='medium'>
          {formatters.MONTH_DAY(moment(payload[0].payload.date))}
        </Texto>
        <Vertical>
          <Horizontal className='pb-1'>
            <Texto appearance='medium' weight='bold' className='mr-4'>
              Pace
            </Texto>
            <Texto align='right' className='flex-1' weight='bold'>
              {addCommasToNumber(payload[0].value * 100, 0)}%
            </Texto>
          </Horizontal>
          <Horizontal className='pb-1'>
            <Texto appearance='medium' weight='bold' className='pr-4'>
              Loaded
            </Texto>
            <Texto align='right' className='flex-1' weight='bold'>
              {addCommasToNumber(payload[1].value, 0)}
            </Texto>
          </Horizontal>

          {!!payload[1].payload.scheduled && (
            <Horizontal className='pb-1'>
              <Texto appearance='medium' weight='bold' className='pr-2'>
                Scheduled
              </Texto>
              <Texto align='right' className='flex-1' weight='bold'>
                {addCommasToNumber(payload[1].payload.scheduled, 0)}
              </Texto>
            </Horizontal>
          )}
          {!!payload[1].payload.directive_min && (
            <Horizontal className='pb-1'>
              <Texto appearance='medium' weight='bold' className='pr-2'>
                Directive Min.
              </Texto>
              <Texto align='right' className='flex-1' weight='bold'>
                {addCommasToNumber(
                  payload[1].payload.directive_min + payload[1].payload.scheduled + payload[1].payload.loaded,
                  0
                )}
              </Texto>
            </Horizontal>
          )}
        </Vertical>
      </div>
    )
  }

  return null
}
const PriceLoadedTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className=' bg-1 p-4'>
        <Texto category='h6' appearance='medium'>
          {payload[0].payload.date}
        </Texto>
        <Vertical>
          <Horizontal className='pb-1'>
            <Texto appearance='medium' weight='bold' className='pr-4'>
              Loaded
            </Texto>
            <Texto align='right' className='flex-1' weight='bold'>
              {addCommasToNumber(payload[0].value, 0)}
            </Texto>
          </Horizontal>

          {payload[1]?.value !== 0 && (
            <Horizontal className='pb-1'>
              <Texto appearance='medium' weight='bold' className='pr-2'>
                Scheduled
              </Texto>
              <Texto align='right' className='flex-1' weight='bold'>
                {addCommasToNumber(payload[1].value, 0)}
              </Texto>
            </Horizontal>
          )}
          {payload[2]?.value !== 0 && (
            <Horizontal className='pb-1'>
              <Texto appearance='medium' weight='bold' className='pr-2'>
                Directive Loaded
              </Texto>
              <Texto align='right' className='flex-1' weight='bold'>
                {addCommasToNumber(payload[0].value + payload[1].value + payload[2].value, 0)}
              </Texto>
            </Horizontal>
          )}
        </Vertical>
      </div>
    )
  }

  return null
}
